import {
  getCompanyFeatureAccessRequest,
  parseFeatureAccessError,
  updateCompanyFeatureAccessRequest,
} from 'api/requests/feature-access-requests';
import { FeatureAccessFeature } from 'constants/FeatureAccessFeature';
import { useCallback, useRef, useState } from 'react';
import {
  FEATURE_ACCESS__COMPANY_NOT_FOUND,
  FEATURE_ACCESS__CONCURRENT_CHANGE,
  FEATURE_ACCESS__PANEL_LOAD_ERROR,
  GENERAL__ERROR_MESSAGE,
} from 'translations/constants';
import { ICompanyFeatureAccess } from 'types/feature-access/IFeatureAccess';
import {
  FeatureStateMap,
  getFeatureAccessErrorKey,
  getSaveOutcome,
  IFeatureAccessRow,
  SaveOutcome,
  toFeatureStateMap,
  toFeatureStates,
} from '../utils';

export type PanelStatus = 'closed' | 'loading' | 'ready' | 'loadError';

export interface UseFeatureAccessPanelCallbacks {
  onSaved: (company: ICompanyFeatureAccess, outcome: SaveOutcome) => void;
  onClosed: () => void;
  onNotFound: () => void;
  onStale: () => void;
  /** A save failed after the user closed the panel or moved on to another company. */
  onError: (messageKey: string) => void;
}

export const useFeatureAccessPanel = (callbacks: UseFeatureAccessPanelCallbacks) => {
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [status, setStatus] = useState<PanelStatus>('closed');
  const [loaded, setLoaded] = useState<FeatureStateMap | null>(null);
  const [draft, setDraft] = useState<FeatureStateMap | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loadErrorKey, setLoadErrorKey] = useState<string | null>(null);
  const [saveErrorKey, setSaveErrorKey] = useState<string | null>(null);
  // Refs, not state: the double-click guard and the stale-response guard must see the new value
  // before React re-renders.
  const savingRef = useRef(false);
  const activeCompanyRef = useRef<number | null>(null);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const load = useCallback(async (id: number) => {
    setStatus('loading');
    setLoadErrorKey(null);
    try {
      const company = await getCompanyFeatureAccessRequest(id);
      if (activeCompanyRef.current !== id) return;
      const state = toFeatureStateMap(company.features);
      setCompanyName(company.companyName);
      setLoaded(state);
      setDraft(state);
      setStatus('ready');
    } catch (error) {
      if (activeCompanyRef.current !== id) return;
      setLoadErrorKey(getFeatureAccessErrorKey(parseFeatureAccessError(error), FEATURE_ACCESS__PANEL_LOAD_ERROR));
      setStatus('loadError');
    }
  }, []);

  const reset = useCallback(() => {
    activeCompanyRef.current = null;
    setCompanyId(null);
    setStatus('closed');
    setLoaded(null);
    setDraft(null);
    setLoadErrorKey(null);
    setSaveErrorKey(null);
  }, []);

  const open = useCallback(
    (row: IFeatureAccessRow) => {
      activeCompanyRef.current = row.companyId;
      setCompanyId(row.companyId);
      setCompanyName(row.companyName);
      setSaveErrorKey(null);
      load(row.companyId);
    },
    [load],
  );

  const retryLoad = useCallback(() => {
    if (activeCompanyRef.current !== null) load(activeCompanyRef.current);
  }, [load]);

  const toggle = useCallback((feature: FeatureAccessFeature) => {
    setDraft(previous => (previous ? { ...previous, [feature]: !previous[feature] } : previous));
  }, []);

  const close = useCallback(() => {
    reset();
    callbacksRef.current.onClosed();
  }, [reset]);

  const save = useCallback(async () => {
    const id = activeCompanyRef.current;
    if (savingRef.current || id === null || status !== 'ready' || !draft || !loaded) return;
    savingRef.current = true;
    setIsSaving(true);
    setSaveErrorKey(null);
    // The user can close the panel, or open another company, while the save is in flight. The outcome is
    // still reported, but panel state is only touched while the panel still shows the company that was saved.
    const stillShowing = () => activeCompanyRef.current === id;
    try {
      const saved = await updateCompanyFeatureAccessRequest(id, toFeatureStates(draft));
      const outcome = getSaveOutcome(loaded, draft);
      if (stillShowing()) reset();
      callbacksRef.current.onSaved(saved, outcome);
    } catch (error) {
      const parsed = parseFeatureAccessError(error);
      const messageKey = getFeatureAccessErrorKey(parsed, GENERAL__ERROR_MESSAGE);
      if (parsed.key === FEATURE_ACCESS__COMPANY_NOT_FOUND) {
        if (stillShowing()) reset();
        callbacksRef.current.onNotFound();
      } else if (!stillShowing()) {
        if (parsed.key === FEATURE_ACCESS__CONCURRENT_CHANGE) callbacksRef.current.onStale();
        callbacksRef.current.onError(messageKey);
      } else if (parsed.key === FEATURE_ACCESS__CONCURRENT_CHANGE) {
        setSaveErrorKey(FEATURE_ACCESS__CONCURRENT_CHANGE);
        callbacksRef.current.onStale();
        load(id);
      } else {
        // TIME_REGISTRATION_RUNNING, 5xx and timeouts: keep the panel and the user's change, and offer a retry.
        setSaveErrorKey(messageKey);
      }
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [draft, loaded, status, reset, load]);

  return {
    companyId,
    companyName,
    status,
    draft,
    isSaving,
    loadErrorKey,
    saveErrorKey,
    open,
    retryLoad,
    toggle,
    save,
    close,
  };
};

export type UseFeatureAccessPanelResult = ReturnType<typeof useFeatureAccessPanel>;
